/**
 * engineNormal_Merge.ts - MergeTemplates implementation for EngineNormal
 * Pure ES Module version
 */

import { LogLevel, Logger } from '../../../../Arshu/dist/logger/logger.js';
import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { DynamicResults } from '../../model/dynamicResults.js';
import { MergeResult } from '../../model/mergeResult.js';
import { TransientStore } from '../../registry/transientStore.js';

import {
    createFlowStack,
    getMainTemplate,
    mergeMainTemplateJson,
    buildOperationsMetadata,
    getTemplateWithJson,
    mergeJsonIntoTemplate,
    collectUnresolvedMarkers
} from './engineNormal_Helper.js';
import type { MergeBoundCallbacks } from './engineNormal_Helper.js';
import { replaceTemplatePlaceholders } from './engineNormal_Merge_TemplateTraversal.js';

import type { EngineNormal } from './engineNormal.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';
import { JsonObject, JsonValue } from '../../app/json/jsonValue.js';
import { CommonUtil } from '../../common/commonUtil.js';
import type { OperationsMetadata } from '../../model/operationsMetadata.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import { JsonUtility } from '../../app/jsonUtility.js';
import { SensitiveArtifactRedactor } from '../../common/sensitiveArtifactRedactor.js';
import { SensitiveDataRedactor } from '@arshu/common';

/**
 * Merges templates by replacing placeholders with corresponding HTML
 * This is a hybrid method that processes both slotted templates and simple placeholders
 * JSON files with matching names are automatically merged with HTML templates before processing
 * Uses the loader provided at construction time.
 * Returns MergeResult with: (appFile, componentHtml, operations, dynamicData, dynamicView, dynamicAction)
 */
export const mergeMethods = {
    mergeTemplates(this: EngineNormal, appSite: string, appView: string | null, genOperationJson: boolean, genComponentHtml: boolean, contextJson: JsonObject | null, timing: PhaseTiming, configJson: JsonObject | null = null, actionMode: boolean = false, clientMode: boolean = false): MergeResult {
        const sensitiveScope = SensitiveDataRedactor.beginScope(JsonUtility.getSensitiveStringValues(configJson));

        Logger.flow(`MergeTemplates: enter appSite=${appSite} appView=${appView || ''} actionMode=${actionMode}`, "Normal.MergeLifecycle");
        // AppSite-triggered resolution (loader-owned, mirrors applyDynamicDataHandlers): an IAppSite handler
        // may redefine the AppSite/AppView before any appfile/template resolution. Unchanged when none applies.
        const { appSiteResolved, navigationTrace, appFileResult, mergeContextJson, mergeConfigJson } = timing.measure(TimingPhase.Resolve, () => {
            const appSiteResolved = this._loader!.applyDynamicAppSiteHandlers(appSite, appView, contextJson ?? new JsonObject(), configJson, timing);
            appSite = appSiteResolved.appSite;
            appView = appSiteResolved.appView;
            // Drain the per-merge navigation trace (ordered IAppSite hops) accumulated at the merge top.
            const navigationTrace = this._loader!.getAndClearNavigationTrace();
            const appFileResult = this._loader!.getAppFile(appSite, appView || '');
            appView = appFileResult.validatedAppView;
            const { mergeContextJson, mergeConfigJson } = this.prepareProtectedMergeContext(appSite, appView, appFileResult.appFile, contextJson, configJson);
            return { appSiteResolved, navigationTrace, appFileResult, mergeContextJson, mergeConfigJson };
        });
        const appFile: string = appFileResult.appFile;
        Logger.flow(`MergeTemplates: appFile=${appFile}`, "Normal.MergeLifecycle");

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`MergeTemplates called: appSite=${appSite}, appFile=${appFile}, appView=${appView || 'null'}`, 'EngineNormal');
        }

        const dynamicResults = timing.measure(TimingPhase.Bind, () => this.initializeDynamicMergeState(appSite, appFile, appView, actionMode));
        const componentHtml = new JsonObject();

        try {
            const flowStack = createFlowStack(appFile);
            const { contentHtml, mainJson, resolvedAppSite } = getMainTemplate(this, appSite, appFile, appView, flowStack, timing, mergeConfigJson, clientMode);
            if (!contentHtml) {
                Logger.flow(`MergeTemplates: mainTemplate=missing`, "Normal.MergeLifecycle");
                Logger.warn(`Main template not found for appSite=${appSite}, appFile=${appFile}`, 'EngineNormal');
                componentHtml.set(appFile, JsonValue.string(''));
                const missingResult = new MergeResult(appFile, componentHtml);
                missingResult.resolvedAppSite = appSiteResolved.appSite;
                missingResult.resolvedAppView = appSiteResolved.appView;
                missingResult.navigationTrace = navigationTrace;
                timing.measure(TimingPhase.Assemble, () => SensitiveArtifactRedactor.redact(missingResult));
                return missingResult;
            }
            Logger.flow(`MergeTemplates: mainTemplate=found`, "Normal.MergeLifecycle");

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Main template found, html size: ${contentHtml.length}`, 'EngineNormal');
            }

            const operations: TemplateOperation[] = [];
            const templateKey = `${resolvedAppSite}_${appFile}`;
            Logger.flow(`MergeTemplates: mainMerge=enter templateKey=${templateKey}`, "Normal.MergeLifecycle");
            let processedHtml = mergeMainTemplateJson(this, contentHtml, mainJson, templateKey, appFile, operations, mergeContextJson, mergeConfigJson, timing);

            // clientMode and timing are invariant across the whole merge, so they are captured in the bound
            // closures (the node equivalent of C# threading them through every recursion method) — every
            // template load via this callback carries them to getTemplateWithView → applyClientHandler.
            const getTemplateWithJsonBound = (appSiteArg: string, currentTemplateName: string, placeholderName: string, appViewArg: string | null, flowStackArg: string[] | null, configJsonArg: JsonObject | null) =>
                getTemplateWithJson(this, appSiteArg, currentTemplateName, placeholderName, appViewArg, timing, flowStackArg, configJsonArg, clientMode);
            const mergeJsonIntoTemplateBound = (html: string, json: JsonObject | null, placeholderName: string, resolvedTemplateName: string | null, resolvedAppSite: string | null, ops: TemplateOperation[], ctxJson: JsonObject | null, cfgJson: JsonObject | null) =>
                mergeJsonIntoTemplate(this, html, json, placeholderName, resolvedTemplateName, resolvedAppSite, ops, ctxJson, timing, cfgJson);

            Logger.flow(`MergeTemplates: iterativeExpansion=enter`, "Normal.MergeLifecycle");
            processedHtml = this.executeMergeIterativeExpansion(processedHtml, appSite, appFile, appView, templateKey, operations, componentHtml, genComponentHtml, mergeContextJson, flowStack, { getTemplateWithJsonBound, mergeJsonIntoTemplateBound }, mergeConfigJson, timing);

            const result = timing.measure(TimingPhase.Assemble, () => {
                Logger.flow(`MergeTemplates: exit components=${componentHtml.size + 1}`, "Normal.MergeLifecycle");
                componentHtml.set(appFile, JsonValue.string(processedHtml));
                this.unescapeProtectedDataValues(componentHtml);

                // Server generation (§2B.4): join the Embedded client-data islands into the FINAL page HTML as a
                // SEPARATE DataHtml — one island per component keyed by component name, sourced from the raw the
                // preparers recorded — inserted before the last </body> (or appended for a fragment), AFTER the
                // unescape pass so the raw is never rescanned/mutated. Kept out of the reusable component template
                // so the client can reconstruct the clean component and re-attach the island reproducibly.
                componentHtml.set(appFile, JsonValue.string(
                    CommonUtil.appendClientDataIslands(componentHtml.getValue(appFile)?.asString() ?? '', dynamicResults.dynamicClientDataResults)));

                const inheritanceOps = this._loader!.getAndClearInheritanceOperations();
                const opsMetadata = buildOperationsMetadata(genOperationJson, appSite, appFile, appView, `${resolvedAppSite}_${appFile}`, operations, inheritanceOps, timing);
                const { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient } = this.collectDynamicHandlerResults(dynamicResults, timing);

                const assembled = new MergeResult(appFile, componentHtml, opsMetadata as OperationsMetadata | null, dynamicData, dynamicView, dynamicAction);
                assembled.dynamicClientData = dynamicClientData;
                assembled.dynamicClient = dynamicClient;
                assembled.resolvedAppSite = appSiteResolved.appSite;
                assembled.resolvedAppView = appSiteResolved.appView;
                assembled.navigationTrace = navigationTrace;
                return assembled;
            });
            timing.measure(TimingPhase.Assemble, () => SensitiveArtifactRedactor.redact(result));
            return result;
        } catch (ex) {
            Logger.error(`Merge failed: ${(ex as Error).message}`, 'EngineNormal');
            Logger.fallback("F-ENG1", `merge exception surfaced: appSite=${appSite} appFile=${appFile} ex=${(ex as Error).message}`);
            throw ex;
        } finally {
            sensitiveScope.dispose();
            this._loader!.setActionMode(false);
        }
    },

    prepareProtectedMergeContext(this: EngineNormal, appSite: string, appView: string | null, appFile: string, contextJson: JsonObject | null, configJson: JsonObject | null): { mergeContextJson: JsonObject | null; mergeConfigJson: JsonObject | null } {
        return {
            mergeContextJson: contextJson ? this.createProtectedMergeContext(appSite, appView, appFile, 'Normal', contextJson) : null,
            mergeConfigJson: this.createProtectedMergeConfig(configJson)
        };
    },

    initializeDynamicMergeState(this: EngineNormal, appSite: string, appFile: string, appView: string | null, actionMode: boolean): DynamicResults {
        const dynamicResults = new DynamicResults();
        this._loader!.setDynamicResults(dynamicResults);
        this._loader!.setActionMode(actionMode);
        const transientStore = new TransientStore();
        this._loader!.setTransientStore(transientStore);
        return dynamicResults;
    },

    executeMergeIterativeExpansion(this: EngineNormal, processedHtml: string, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: TemplateOperation[], componentHtml: JsonObject, genComponentHtml: boolean, mergeContextJson: JsonObject | null, flowStack: string[], boundCallbacks: MergeBoundCallbacks, mergeConfigJson: JsonObject | null, timing: PhaseTiming): string {
        return timing.measure(TimingPhase.Render, () => {
        const { getTemplateWithJsonBound, mergeJsonIntoTemplateBound } = boundCallbacks;
        let previous: string;
        const maxPasses = this._loader!.maxMergeIterations;
        let actualPasses = 0;
        let stabilized = false;
        for (let pass = 0; pass < maxPasses; pass++) {
            previous = processedHtml;
            actualPasses = pass + 1;

            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`Pass ${actualPasses}, current size: ${processedHtml.length}`, 'EngineNormal');
            }
            Logger.flow(`IterativeExpansion: pass=${actualPasses} sizeBefore=${processedHtml.length}`, "Normal.TemplateRecursion");

            processedHtml = this.mergeTemplateSlots(processedHtml, appSite, appFile, appView, templateKey, operations, 0, componentHtml, genComponentHtml, mergeContextJson, flowStack, getTemplateWithJsonBound, mergeJsonIntoTemplateBound, replaceTemplatePlaceholders, timing, mergeConfigJson);
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`After slot merge: ${processedHtml.length} chars`, 'EngineNormal');
            }
            Logger.flow(`IterativeExpansion: pass=${actualPasses} afterSlots=${processedHtml.length}`, "Normal.TemplateRecursion");

            processedHtml = replaceTemplatePlaceholders(this, processedHtml, appSite, appFile, appView, templateKey, operations, 0, componentHtml, genComponentHtml, mergeContextJson, flowStack, getTemplateWithJsonBound, mergeJsonIntoTemplateBound, timing, mergeConfigJson);
            if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                Logger.debug(`After placeholder replacement: ${processedHtml.length} chars`, 'EngineNormal');
            }
            Logger.flow(`IterativeExpansion: pass=${actualPasses} afterPlaceholders=${processedHtml.length}`, "Normal.TemplateRecursion");

            if (processedHtml === previous) {
                if (Logger.getLogLevel() <= LogLevel.DEBUG) {
                    Logger.debug(`No changes in pass ${actualPasses}, stopping`, 'EngineNormal');
                }
                Logger.flow(`IterativeExpansion: stable=true pass=${actualPasses}`, "Normal.TemplateRecursion");
                stabilized = true;
                break;
            }
        }

        // Exhausting the budget means the template never converged, so the accumulated HTML still holds
        // unresolved markers. Returning it would hand the caller silently wrong output, so raise instead.
        if (!stabilized) {
            throw new Error(
                `Template merge did not stabilize after ${maxPasses} passes for appSite=${appSite} appFile=${appFile} ` +
                `templateKey=${templateKey}. Unresolved markers: ${collectUnresolvedMarkers(processedHtml)}. ` +
                'Possible circular template reference.');
        }

        if (Logger.getLogLevel() <= LogLevel.DEBUG) {
            Logger.debug(`MergeTemplates complete after ${actualPasses} passes: output size=${processedHtml.length}`, 'EngineNormal');
        }

        return JsonMergeUtil.unescapeDataValues(processedHtml);
        });
    },

    unescapeProtectedDataValues(this: EngineNormal, componentHtml: JsonObject): void {
        for (const [key, value] of componentHtml) {
            componentHtml.set(key, JsonValue.string(JsonMergeUtil.unescapeDataValues(value.asString() || '')));
        }
    },

    collectDynamicHandlerResults(this: EngineNormal, dynamicResults: DynamicResults, timing: PhaseTiming): { dynamicData: JsonObject; dynamicView: JsonObject; dynamicAction: JsonObject; dynamicClientData: JsonObject; dynamicClient: JsonObject } {
        return timing.measure(TimingPhase.Assemble, () => {
        const dynamicData = dynamicResults.dynamicDataResults;
        const dynamicView = dynamicResults.dynamicViewResults;
        const dynamicAction = dynamicResults.dynamicActionResults;
        const dynamicClientData = dynamicResults.dynamicClientDataResults;
        const dynamicClient = dynamicResults.dynamicClientResults;

        for (const [key, value] of dynamicView) {
            dynamicView.set(key, JsonValue.string(JsonMergeUtil.unescapeDataValues(value.asString() || '')));
        }

        return { dynamicData, dynamicView, dynamicAction, dynamicClientData, dynamicClient };
        });
    }
};
